import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { QRCodeSVG } from 'qrcode.react';

import {
  attachInviteAttributionToUrl,
  getMyInviteLink,
} from '../../api/inviteApi';
import { useToast } from '../../components/Toast';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import { useAuth } from '../../contexts/AuthContext';
import { downloadBlobFile } from '../../utils/downloadUtils';
import { trackInviteShare } from '../../utils/eventTracker';
import {
  copyToClipboard,
  shareContent,
  shareToKakao,
} from '../../utils/shareUtils';
import './InvitePage.css';


const InvitePage = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showQrFullscreen, setShowQrFullscreen] = useState(false);
  const loadRequestRef = useRef(0);
  const qrCodeRef = useRef(null);
  const qrFullscreenRef = useRef(null);

  const loadInvite = useCallback(async () => {
    if (!currentUser) return;
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const result = await getMyInviteLink(currentUser);
      if (loadRequestRef.current === requestId) {
        setInvite(result);
        setShowQr(false);
        setShowQrFullscreen(false);
      }
    } catch (loadError) {
      if (loadRequestRef.current === requestId) {
        setInvite(null);
        setError(
          loadError?.message
          || '초대 링크를 만들지 못했어요. 잠시 후 다시 시도해주세요.'
        );
      }
    } finally {
      if (loadRequestRef.current === requestId) setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadInvite();
    return () => {
      loadRequestRef.current += 1;
    };
  }, [loadInvite]);

  // QR 크게 보기의 ESC·스크림·스크롤 잠금·포커스 복귀는 Dialog 프리미티브가 맡는다.

  const handleShare = async () => {
    if (!invite?.share_url || sharing) return;
    setSharing(true);
    const accountShareUrl = attachInviteAttributionToUrl(
      invite.share_url,
      invite.share_url,
      'account',
    );
    const shareData = {
      title: '선생님께 왓퀴즈를 추천해요',
      text: '생기부, PPT, 학습지, 채점을 한 곳에서 빠르게 만들어보세요.',
      url: accountShareUrl,
    };
    let result = await shareToKakao(shareData);
    if (!result.success) result = await shareContent(shareData);
    setSharing(false);

    if (result.success) {
      trackInviteShare('account', result.method, true);
      showToast(
        result.method === 'kakao'
          ? '카카오톡 공유 화면을 열었어요'
          : result.method === 'native_share'
            ? '공유 화면을 열었어요'
            : '초대 링크를 복사했어요',
        'success',
      );
    } else if (!result.error?.includes('취소')) {
      showToast('공유하지 못했어요. 다시 시도해주세요.', 'error');
    }
  };

  const handleCopy = async () => {
    if (!invite?.share_url) return;
    const accountShareUrl = attachInviteAttributionToUrl(
      invite.share_url,
      invite.share_url,
      'account',
    );
    const copied = await copyToClipboard(accountShareUrl);
    if (copied) trackInviteShare('account', 'clipboard', true);
    showToast(
      copied
        ? '초대 링크를 복사했어요'
        : '링크를 복사하지 못했어요. 다시 시도해주세요.',
      copied ? 'success' : 'error',
    );
  };

  const handleToggleQr = () => {
    if (!invite?.share_url) return;
    if (!showQr) trackInviteShare('account', 'qr', true);
    if (showQr) setShowQrFullscreen(false);
    setShowQr((current) => !current);
  };

  const downloadQrSvg = (sourceSvg) => {
    if (!sourceSvg) return;
    try {
      const svg = sourceSvg.cloneNode(true);
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const source = new XMLSerializer().serializeToString(svg);
      const saved = downloadBlobFile(
        new Blob([source], { type: 'image/svg+xml;charset=utf-8' }),
        'whatquiz-friend-invite-qr.svg',
      );
      if (!saved) throw new Error('download_failed');
      trackInviteShare('account', 'qr_download', true);
      showToast('QR 이미지를 저장했어요', 'success');
    } catch (_error) {
      showToast('QR 이미지를 저장하지 못했어요. 다시 시도해주세요.', 'error');
    }
  };

  const handleShowQrFullscreen = () => {
    trackInviteShare('account', 'qr_fullscreen', true);
    setShowQrFullscreen(true);
  };

  const formatEventDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });
  };

  const rewardPolicy = invite?.reward_policy || {};
  const signupEnabled = (
    rewardPolicy.signup_enabled
    ?? Boolean(invite?.rewards_enabled)
  );
  const conversionEnabled = Boolean(rewardPolicy.conversion_enabled);
  const paymentDiscountEnabled = Boolean(
    rewardPolicy.payment_discount_enabled
  );
  const programEnabled = Boolean(
    invite?.program_enabled
    ?? (signupEnabled || conversionEnabled || paymentDiscountEnabled)
  );

  return (
    <main className="invite-page">
      <Helmet>
        <title>친구 초대 | 왓퀴즈</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="invite-hero">
        <div className="invite-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
            <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h1>좋은 도구를 동료 선생님께 알려주세요</h1>
        <p>내 초대 링크 하나로 왓퀴즈를 간편하게 소개할 수 있어요.</p>
      </section>

      <section className="invite-card" aria-live="polite">
        {loading ? (
          <div className="invite-skeleton" data-testid="invite-loading">
            <div className="invite-skeleton-line invite-skeleton-line-wide" />
            <div className="invite-skeleton-line" />
            <div className="invite-skeleton-button" />
          </div>
        ) : error ? (
          <div className="invite-error" role="alert">
            <strong>초대 링크를 불러오지 못했어요</strong>
            <p>{error}</p>
            <button
              className="invite-primary-button invite-retry-button"
              onClick={loadInvite}
              type="button"
            >
              다시 시도하기
            </button>
          </div>
        ) : (
          <>
            <div className="invite-value">
              <strong>
                {programEnabled
                  ? '공유하고 함께 혜택 받기'
                  : '선생님 전용 링크를 준비했어요'}
              </strong>
              <p>
                {signupEnabled
                  ? '친구가 새로 가입하면 두 분 모두 PRO 7일을 받아요.'
                  : programEnabled
                    ? '친구가 가입하고 결제하면 혜택이 자동으로 적용돼요.'
                  : '카카오톡이나 교사 단톡방에 바로 보낼 수 있어요.'}
              </p>
            </div>
            <div className="invite-share-actions">
              <button
                className="invite-primary-button"
                disabled={sharing}
                onClick={handleShare}
                type="button"
              >
                {sharing ? '공유 준비 중' : '카카오톡으로 공유하기'}
              </button>
              <button
                className="invite-secondary-button"
                onClick={handleCopy}
                type="button"
              >
                링크 복사
              </button>
            </div>
            <button
              aria-controls="invite-qr-panel"
              aria-expanded={showQr}
              className="invite-qr-toggle"
              onClick={handleToggleQr}
              type="button"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" stroke="currentColor" strokeWidth="2" />
                <path d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z" fill="currentColor" />
              </svg>
              {showQr ? 'QR 닫기' : 'QR로 보여주기'}
            </button>
            {showQr && (
              <div className="invite-qr-panel" id="invite-qr-panel">
                <QRCodeSVG
                  bgColor="#FFFFFF"
                  fgColor="#191F28"
                  level="M"
                  ref={qrCodeRef}
                  size={184}
                  title="왓퀴즈 친구 초대 QR 코드"
                  value={attachInviteAttributionToUrl(
                    invite.share_url,
                    invite.share_url,
                    'account',
                  )}
                />
                <h3>카메라로 찍으면 초대 화면이 열려요</h3>
                <p>교무실에서 화면으로 보여주거나 인쇄해서 공유해보세요.</p>
                <div className="invite-qr-panel-actions">
                  <button
                    className="invite-qr-enlarge"
                    onClick={handleShowQrFullscreen}
                    type="button"
                  >
                    QR 크게 보기
                  </button>
                  <button
                    className="invite-qr-download"
                    onClick={() => downloadQrSvg(qrCodeRef.current)}
                    type="button"
                  >
                    QR 이미지 저장
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {!loading && !error && programEnabled && invite?.stats && (
        <section className="invite-dashboard" aria-labelledby="invite-stats-title">
          <div className="invite-reward-rules">
            <h2>친구와 함께 받는 혜택</h2>
            {signupEnabled && (
              <div>
                <span>가입하면</span>
                <strong>
                  두 분 모두 PRO {rewardPolicy.signup_days_each || 7}일
                </strong>
              </div>
            )}
            {paymentDiscountEnabled && (
              <div>
                <span>친구 첫 결제</span>
                <strong>
                  친구 {Number(
                    rewardPolicy.first_payment_discount || 10000
                  ).toLocaleString()}원 할인
                </strong>
              </div>
            )}
            {conversionEnabled && (
              <div>
                <span>결제 완료</span>
                <strong>
                  나는 PRO {rewardPolicy.conversion_days || 30}일
                </strong>
              </div>
            )}
          </div>

          <h2 id="invite-stats-title">내 초대 현황</h2>
          <div className="invite-stat-grid">
            <div className="invite-stat">
              <strong>{invite.stats.attributed_count || 0}</strong>
              <span>연결된 친구</span>
            </div>
            <div className="invite-stat">
              <strong>{invite.stats.total_reward_days || 0}일</strong>
              <span>받은 PRO</span>
            </div>
            {conversionEnabled && (
              <div className="invite-stat">
                <strong>{invite.stats.pending_conversion_count || 0}</strong>
                <span>첫 결제 대기</span>
              </div>
            )}
          </div>

          {Array.isArray(invite.recent_events)
            && invite.recent_events.length > 0 && (
              <div className="invite-recent">
                <h3>최근 혜택</h3>
                <ul>
                  {invite.recent_events.map((event) => (
                    <li key={event.event_id}>
                      <div>
                        <strong>{event.benefit_label}</strong>
                        <span>
                          {event.type === 'conversion'
                            ? '친구 첫 결제'
                            : '친구 가입'}
                        </span>
                      </div>
                      <time dateTime={event.occurred_at || undefined}>
                        {formatEventDate(event.occurred_at)}
                      </time>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </section>
      )}
      {/* QR 크게 보기 — 교무실 화면으로 보여주는 용도라 md 폭에 QR을 꽉 채운다 */}
      <Dialog
        align="center"
        closeLabel="QR 크게 보기 닫기"
        description="휴대폰 카메라를 QR에 비추면 초대 화면이 열려요."
        eyebrow="왓퀴즈 친구 초대"
        footer={(
          <ModalActions
            primary={{
              label: 'QR 이미지 저장하기',
              onClick: () => downloadQrSvg(qrFullscreenRef.current),
            }}
            testId="invite-qr-fullscreen-actions"
          />
        )}
        onClose={() => setShowQrFullscreen(false)}
        open={Boolean(showQrFullscreen && invite?.share_url)}
        panelClassName="invite-qr-fullscreen"
        size="md"
        testId="invite-qr-fullscreen"
        title="카메라로 찍고 바로 시작하세요"
      >
        {showQrFullscreen && invite?.share_url && (
          <div className="invite-qr-fullscreen-code">
            <QRCodeSVG
              bgColor="#FFFFFF"
              fgColor="#111827"
              level="M"
              ref={qrFullscreenRef}
              size={520}
              title="왓퀴즈 친구 초대 QR 코드 크게 보기"
              value={attachInviteAttributionToUrl(
                invite.share_url,
                invite.share_url,
                'account',
              )}
            />
          </div>
        )}
      </Dialog>
    </main>
  );
};

export default InvitePage;
